import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Fingerprint, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import ChooseLid from './ChooseLid'
import PassphraseCoach from './PassphraseCoach'

interface SecureIdentityStepProps {
  onNext: () => void
}

type SubStep = 'method' | 'choose-lid' | 'passphrase' | 'confirm' | 'passkey'
type AuthMethod = 'passphrase' | 'passkey'

/**
 * SecureIdentityStep - Minimalist security setup
 *
 * "Simplicity is the ultimate sophistication." — Leonardo da Vinci
 */
export default function SecureIdentityStep({ onNext }: SecureIdentityStepProps) {
  const [subStep, setSubStep] = useState<SubStep>('method')
  const [selectedLid, setSelectedLid] = useState<string>('')
  const [passphraseData, setPassphraseData] = useState<{
    passphrase: string
    canonical: string
  } | null>(null)

  const { completeRegistration, isLoading, error, clearError } = useAuthStore()

  const handleLidSelected = useCallback((lid: string) => {
    setSelectedLid(lid)
    setSubStep('passphrase')
  }, [])

  const handlePassphraseReady = useCallback((passphrase: string, canonical: string) => {
    setPassphraseData({ passphrase, canonical })
    setSubStep('confirm')
  }, [])

  const handleConfirmComplete = useCallback(async () => {
    if (!passphraseData) return

    try {
      await completeRegistration({
        password: passphraseData.canonical,
        lidNumber: selectedLid
      })
      onNext()
    } catch {
      // Error handled by store
    }
  }, [passphraseData, selectedLid, completeRegistration, onNext])

  const handlePasskeyRegistration = async () => {
    if (error) clearError()
    try {
      await completeRegistration({ usePasskey: true })
      onNext()
    } catch {
      // Error handled by store
    }
  }

  const handleBack = useCallback(() => {
    if (subStep === 'choose-lid') {
      setSubStep('method')
    } else if (subStep === 'passphrase') {
      setSubStep('choose-lid')
    } else if (subStep === 'confirm') {
      setSubStep('passphrase')
      setPassphraseData(null)
    } else if (subStep === 'passkey') {
      setSubStep('method')
    }
  }, [subStep])

  const handleSelectMethod = useCallback((method: AuthMethod) => {
    if (method === 'passphrase') {
      setSubStep('choose-lid')
    } else {
      setSubStep('passkey')
    }
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-8"
    >
      {/* Back button - only show in sub-steps */}
      {subStep !== 'method' && (
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-xs text-white/30 hover:text-white/60 transition-colors duration-300"
        >
          <ArrowLeft className="w-3 h-3" />
          <span>Back</span>
        </button>
      )}

      {/* Error message */}
      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm text-red-400/80 font-light"
        >
          {error}
        </motion.p>
      )}

      {/* Method Selection */}
      {subStep === 'method' && (
        <motion.div
          key="method"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="space-y-3">
            <h2 className="text-3xl font-extralight tracking-tight text-white">
              Secure your account
            </h2>
            <p className="text-white/40 font-light">
              Choose how you want to protect your identity.
            </p>
          </div>

          <div className="space-y-4">
            {/* Passphrase option */}
            <button
              onClick={() => handleSelectMethod('passphrase')}
              className="w-full text-left p-6 border border-white/10 hover:border-white/20 transition-colors duration-300 group"
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-light text-white">Passphrase</span>
                    <span className="text-[10px] text-white/30 uppercase tracking-widest border border-white/10 px-2 py-0.5">
                      Recommended
                    </span>
                  </div>
                  <p className="text-sm text-white/40 font-light max-w-sm">
                    Create a memorable phrase. Entropy analysis ensures security.
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white/40 group-hover:translate-x-1 transition-all duration-300" />
              </div>
            </button>

            {/* Passkey option */}
            <button
              onClick={() => handleSelectMethod('passkey')}
              className="w-full text-left p-6 border border-white/10 hover:border-white/20 transition-colors duration-300 group"
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <span className="text-lg font-light text-white">Passkey</span>
                  <p className="text-sm text-white/40 font-light max-w-sm">
                    Biometric or hardware key. FIDO2 standard.
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white/40 group-hover:translate-x-1 transition-all duration-300" />
              </div>
            </button>
          </div>
        </motion.div>
      )}

      {/* Choose ΛiD# */}
      {subStep === 'choose-lid' && (
        <motion.div
          key="choose-lid"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="space-y-3">
            <h2 className="text-3xl font-extralight tracking-tight text-white">
              Choose your identifier
            </h2>
            <p className="text-white/40 font-light">
              Your unique Λ number across the ecosystem.
            </p>
          </div>

          <ChooseLid onLidSelected={handleLidSelected} />
        </motion.div>
      )}

      {/* Passphrase step */}
      {subStep === 'passphrase' && (
        <motion.div
          key="passphrase"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <PassphraseCoach
            lidNumber={selectedLid}
            onPassphraseReady={handlePassphraseReady}
            locale="en"
          />
        </motion.div>
      )}

      {/* Confirmation */}
      {subStep === 'confirm' && passphraseData && (
        <motion.div
          key="confirm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="space-y-3">
            <h2 className="text-3xl font-extralight tracking-tight text-white">
              Ready to proceed
            </h2>
            <p className="text-white/40 font-light">
              Your identity is configured and secured.
            </p>
          </div>

          <div className="space-y-6">
            {/* Identity display */}
            <div className="p-6 border border-white/10">
              <p className="text-xs text-white/30 uppercase tracking-widest mb-3">
                Your Identifier
              </p>
              <p className="text-xl font-light text-white font-mono">
                {selectedLid}
              </p>
            </div>

            {/* Security note */}
            <p className="text-xs text-white/30 font-light leading-relaxed">
              Your passphrase is never transmitted. All encryption happens locally on your device.
            </p>

            {/* Complete button */}
            <button
              onClick={handleConfirmComplete}
              disabled={isLoading}
              className="
                w-full py-4
                bg-white text-black text-sm font-medium tracking-wide
                disabled:opacity-20 disabled:cursor-not-allowed
                hover:bg-white/90 active:bg-white/80
                transition-all duration-200
                flex items-center justify-center gap-3
              "
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <span>Complete Setup</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      )}

      {/* Passkey Registration */}
      {subStep === 'passkey' && (
        <motion.div
          key="passkey"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="space-y-3">
            <h2 className="text-3xl font-extralight tracking-tight text-white">
              Register passkey
            </h2>
            <p className="text-white/40 font-light">
              Your device will prompt you for biometric confirmation.
            </p>
          </div>

          <div className="py-12 flex flex-col items-center justify-center space-y-6">
            <div className="w-20 h-20 border border-white/10 flex items-center justify-center">
              <Fingerprint className="w-10 h-10 text-white/40" />
            </div>
            <p className="text-sm text-white/30 text-center max-w-xs">
              Touch ID, Face ID, or hardware security key
            </p>
          </div>

          <button
            onClick={handlePasskeyRegistration}
            disabled={isLoading}
            className="
              w-full py-4
              bg-white text-black text-sm font-medium tracking-wide
              disabled:opacity-20 disabled:cursor-not-allowed
              hover:bg-white/90 active:bg-white/80
              transition-all duration-200
              flex items-center justify-center gap-3
            "
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Fingerprint className="w-4 h-4" />
                <span>Register Passkey</span>
              </>
            )}
          </button>
        </motion.div>
      )}
    </motion.div>
  )
}
